import type { AgentBuilderKpiCard, AgentBuilderReportData, DashboardValueFormat, DashboardWidget, WidgetHydrationState } from './types'
import {
  deriveChartData,
  deriveKpiCards,
  formatColumnValue,
  formatFieldDisplayName,
  formatInsightValue,
  resolveColumnFormat,
  resolveInsightValueFormat,
  type InsightValueFormat,
} from './vizUtils'
import { formatFieldValue, isNumericFormatKind, type FieldFormat } from '../../lib/fieldFormat'
import { findByLabel, labelsMatch, normalizeLabel, stripTableQualifier } from './labelMatching'
import { findDrilldownColumn } from './drilldownHierarchy'
import { resolveDateDimensionKey, sortByCalendarValue } from './sortUtils'

function getColumnsFromRows(rows: Record<string, unknown>[], widget: DashboardWidget, widgetState: WidgetHydrationState) {
  if (widgetState.columns?.length) {
    return widgetState.columns
  }

  const keys = new Set<string>()
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => keys.add(key))
  })

  if (keys.size > 0) {
    return Array.from(keys)
  }

  return [
    ...(widget.dimensions ?? []).map((dimension) => dimension?.name).filter((name): name is string => Boolean(name)),
    ...(widget.measures ?? []).map((measure) => measure?.name).filter((name): name is string => Boolean(name)),
  ]
}

function normalizeCellValue(value: unknown): string | number | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'number') return value
  if (typeof value === 'string') return value
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return JSON.stringify(value)
}

function getActiveDimensions(widget: DashboardWidget, widgetState: WidgetHydrationState) {
  const daxDimensions = widget.daxGenerationRequest?.dimensions ?? []
  const preferred = widgetState.groupByDimension ?? widget.rowDimension ?? daxDimensions[0] ?? null
  const dimensions = [
    preferred,
    ...daxDimensions,
    // The field ref, not the display name: a loaded dashboard renames "Jobs[Job Category]" to "Job Category".
    ...(widget.dimensions ?? []).map((dimension) => dimension?.value || dimension?.name),
  ].filter((value): value is string => Boolean(value))

  // The field ref "'Jobs'[Job Category]" and the name "Jobs[Job Category]" are one dimension.
  const seen = new Set<string>()
  return dimensions.filter((dimension) => {
    const key = normalizeLabel(dimension)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * The returned column to put back in calendar order: set when the widget is sorted by a date or
 * month dimension. The server sorts "Sep 2025"-style labels as text, so the order is fixed here
 * (sortByCalendarValue leaves values that are not all calendar values as they came).
 */
function resolveCalendarSort(widget: DashboardWidget, widgetState: WidgetHydrationState, columns: string[]) {
  const sortColumn = widgetState.sortColumn
  if (!sortColumn || !resolveDateDimensionKey(widget, sortColumn)) return null
  const column = findDrilldownColumn(columns, sortColumn)
  return column ? { column, direction: widgetState.sortDirection === 'desc' ? 'desc' as const : 'asc' as const } : null
}

function resolveKpiCards(widget: DashboardWidget, widgetState: WidgetHydrationState, columns: string[], rows: Record<string, string | number | null>[]) {
  const backendCards = widgetState.kpiCards ?? []
  const derivedCards = deriveKpiCards(columns, rows)

  if (widget.type === 'kpi' && derivedCards.length > backendCards.length) {
    return derivedCards
  }

  return backendCards.length ? backendCards : derivedCards
}

/** Matches a returned column or card key to a configured measure, with or without its "'Table'[...]" qualifier. */
function findByMeasure<T>(items: T[], field: string, labelsOf: (item: T) => string[]): T | null {
  return findByLabel(items, field, labelsOf) ?? findByLabel(items, stripTableQualifier(field), labelsOf)
}

const measureLabels = (measure: string) => [measure, stripTableQualifier(measure)]

/**
 * Every name a configured measure can come back under. cards[]/columns[].measure holds the catalog
 * value ("Invoice Billing") while the API keys columns and cards by the catalog name ("Billing"),
 * which the widget's measures[] entry for that value carries.
 */
function measureAliases(widget: DashboardWidget, measure: string) {
  const aliases = measureLabels(measure)
  for (const option of widget.measures ?? []) {
    if (!option?.name) continue
    if (labelsMatch(option.value, measure) || labelsMatch(stripTableQualifier(option.value), stripTableQualifier(measure))) {
      aliases.push(option.name, stripTableQualifier(option.name))
    }
  }
  return Array.from(new Set(aliases))
}

/** The item whose key (via `keysOf`) is one of the configured measure's aliases. */
function findConfiguredMeasure<T>(items: T[], widget: DashboardWidget, measure: string, keysOf: (item: T) => string[]): T | null {
  for (const alias of measureAliases(widget, measure)) {
    const match = findByMeasure(items, alias, keysOf)
    if (match) return match
  }
  return null
}

// The contract's format carries a type and an optional abbreviation, so decimals and currency symbol come from the formatter defaults.
function toFieldFormat(format: DashboardValueFormat | undefined): FieldFormat | null {
  if (!format?.type) return null
  return format.abbreviate ? { kind: format.type, abbreviate: format.abbreviate } : { kind: format.type }
}

/**
 * A format stored on Report Builder Options wins over the contract's type; the contract only adds its
 * abbreviation. The contract's type does replace a server format guessed from the measure name
 * (isDefault). When both agree on the kind, the server's decimals and currency symbol are kept.
 */
function withContractFormat(server: FieldFormat | null, contract: DashboardValueFormat | undefined): FieldFormat | null {
  const configured = toFieldFormat(contract)
  if (!configured) return server
  if (server && !server.isDefault) {
    return configured.abbreviate && isNumericFormatKind(server.kind) ? { ...server, abbreviate: configured.abbreviate } : server
  }
  if (!server || server.kind !== configured.kind) return configured
  const { isDefault: _isDefault, ...guessed } = server
  return { ...guessed, ...configured }
}

/**
 * cards[].format and columns[].format override the server-resolved formats for their measures, and the
 * rest keep the server's, so the screen, PDF and insights all read one map. Entries are matched to the
 * returned columns by measure value or catalog name.
 */
function mergeWidgetColumnFormats(widget: DashboardWidget, serverFormats: Record<string, FieldFormat> | null | undefined, columns: string[]) {
  const merged = { ...(serverFormats ?? {}) }
  // Only the first entry per measure sets the shared column format; per-card formats live on the card.
  const seen = new Set<string>()
  for (const entry of [...(widget.cards ?? []), ...(widget.columns ?? [])]) {
    if (!entry.format?.type) continue
    const key = findConfiguredMeasure(columns, widget, entry.measure, measureLabels) ?? entry.measure
    if (seen.has(key)) continue
    seen.add(key)
    const format = withContractFormat(resolveColumnFormat(merged, key), entry.format)
    if (format) merged[key] = format
  }
  return Object.keys(merged).length ? merged : null
}

function resolveColumnLabels(widget: DashboardWidget, columns: string[]) {
  const labels: Record<string, string> = {}
  for (const entry of widget.columns ?? []) {
    const label = entry.label?.trim()
    const column = label ? findConfiguredMeasure(columns, widget, entry.measure, measureLabels) : null
    if (label && column) labels[column] = label
  }
  return Object.keys(labels).length ? labels : null
}

const cardKeys = (card: AgentBuilderKpiCard) => [card.measureKey, card.measureLabel, card.label]
  .filter((key): key is string => Boolean(key))
  .flatMap(measureLabels)

function configureCard(
  card: AgentBuilderKpiCard,
  entry: NonNullable<DashboardWidget['cards']>[number],
  columnFormats: Record<string, FieldFormat> | null,
): AgentBuilderKpiCard {
  const label = entry.label?.trim()
  const format = entry.format?.type
    ? withContractFormat(resolveColumnFormat(columnFormats, card.measureKey || card.measureLabel), entry.format)
    : null
  return {
    ...card,
    ...(label ? { displayLabel: label } : {}),
    ...(format ? { format } : {}),
    ...(entry.format?.negativeColor ? { negativeColor: entry.format.negativeColor } : {}),
  }
}

/**
 * Applies cards[] (label, format, negativeColor) by position. When each card is a different measure,
 * the cards follow the contract's order and a measure listed twice is shown twice with its own label
 * and format; cards no entry names keep their place after them. Cards that repeat one measure
 * (one per dimension row) all take the first matching entry.
 */
function applyCardConfig(cards: AgentBuilderKpiCard[], widget: DashboardWidget, columnFormats: Record<string, FieldFormat> | null): AgentBuilderKpiCard[] {
  const configured = widget.cards ?? []
  if (configured.length === 0 || cards.length === 0) return cards

  const distinctMeasures = new Set(cards.map((card) => normalizeLabel(card.measureKey || card.measureLabel || card.label)))
  if (distinctMeasures.size < cards.length) {
    return cards.map((card) => {
      const entry = configured.find((item) => findConfiguredMeasure([card], widget, item.measure, cardKeys))
      return entry ? configureCard(card, entry, columnFormats) : card
    })
  }

  const used = new Set<AgentBuilderKpiCard>()
  const ordered: AgentBuilderKpiCard[] = []
  for (const entry of configured) {
    const card = findConfiguredMeasure(cards, widget, entry.measure, cardKeys)
    if (!card) continue
    used.add(card)
    ordered.push(configureCard(card, entry, columnFormats))
  }
  return [...ordered, ...cards.filter((card) => !used.has(card))]
}

function normalizeRows(columns: string[], rows: Record<string, unknown>[]) {
  return rows.map((row) => (
    Object.fromEntries(columns.map((column) => [column, normalizeCellValue(row[column])])) as Record<string, string | number | null>
  ))
}

/** The points a chart draws from `rows` when it has no `chartData` of its own. */
export function deriveChartDataFromRows(columns: string[], rows: Record<string, unknown>[]) {
  const keys = columns.length ? columns : Array.from(new Set(rows.flatMap((row) => Object.keys(row))))
  return deriveChartData(keys, normalizeRows(keys, rows))
}

export function mapWidgetToAgentBuilderData(widget: DashboardWidget, widgetState: WidgetHydrationState): AgentBuilderReportData {
  const columns = getColumnsFromRows(widgetState.rows, widget, widgetState)
  const calendarSort = resolveCalendarSort(widget, widgetState, columns)
  const normalizedRows = normalizeRows(columns, widgetState.rows)
  const rows = calendarSort
    ? sortByCalendarValue(normalizedRows, (row) => row[calendarSort.column], calendarSort.direction)
    : normalizedRows
  const columnFormats = mergeWidgetColumnFormats(widget, widgetState.columnFormats, columns)
  const backendChartData = widgetState.chartData?.length ? widgetState.chartData : null

  const visualizationType = widget.type === 'groupedBar'
    ? 'GroupedBar'
    : widget.type === 'line'
      ? 'Line'
      : widget.type === 'kpi'
        ? 'KPI'
        : widget.type === 'donut'
          ? 'Donut'
          : widget.type === 'gauge'
            ? 'Gauge'
            : widget.type === 'table' || widget.type === 'dataBarTable' || widget.type === 'pivotTable'
              ? 'Table'
              : 'Bar'

  return {
    title: widget.title,
    visualizationType,
    columns,
    dimensions: getActiveDimensions(widget, widgetState),
    comparePreviousYear: widget.comparePreviousYear,
    rows,
    chartData: backendChartData && calendarSort
      ? sortByCalendarValue(backendChartData, (point) => point.label, calendarSort.direction)
      : backendChartData ?? deriveChartData(columns, rows),
    comparisonChartData: widgetState.comparisonChartData ?? [],
    kpiCards: applyCardConfig(resolveKpiCards(widget, widgetState, columns, rows), widget, columnFormats),
    appliedFilters: Object.fromEntries((widget.filters ?? []).map((filter) => [filter.fieldName, String(filter.value)])),
    retrievedAt: new Date().toISOString(),
    platformType: 0,
    hasPaginationMetadata: widgetState.hasPaginationMetadata ?? false,
    totalRows: widgetState.totalRows,
    page: widgetState.page,
    pageSize: widgetState.pageSize,
    sortColumn: widgetState.sortColumn ?? null,
    sortDirection: widgetState.sortDirection ?? null,
    groupByDimension: widgetState.groupByDimension ?? null,
    axisMinValue: widgetState.axisMinValue ?? null,
    axisMaxValue: widgetState.axisMaxValue ?? null,
    axisMinByMeasure: widgetState.axisMinByMeasure ?? null,
    axisMaxByMeasure: widgetState.axisMaxByMeasure ?? null,
    columnFormats,
    columnLabels: resolveColumnLabels(widget, columns),
  }
}

export type WidgetInsightData = {
  chartData: Record<string, string>[]
  kpiCards: { label: string; value: string }[]
  valueFormats: Record<string, InsightValueFormat>
}

/**
 * Chart/KPI data for insight generation, with every value formatted as the dashboard displays it
 * (ReportBuilderOptions currency symbol and decimals, fractions as %, thousands separators) and keyed by
 * measure name, so the model quotes the same figures users see instead of raw numbers like 0.792.
 */
export function buildWidgetInsightData(widget: DashboardWidget, widgetState: WidgetHydrationState): WidgetInsightData {
  const data = mapWidgetToAgentBuilderData(widget, widgetState)
  const explicitFormats = new Map<string, NonNullable<DashboardWidget['cards']>[number]['format']>()
  const explicitFormatsByDisplayName = new Map<string, NonNullable<DashboardWidget['cards']>[number]['format']>()
  for (const entry of [...(widget.cards ?? []), ...(widget.columns ?? [])]) {
    if (!entry.format) continue
    explicitFormats.set(entry.measure, entry.format)
    const displayName = formatFieldDisplayName(entry.measure)
    if (!explicitFormatsByDisplayName.has(displayName)) explicitFormatsByDisplayName.set(displayName, entry.format)
  }
  // Configured formats are keyed by the raw measure ("'Table'[Margin]"); fall back to the
  // display name for keys that only carry display text (e.g. backend-provided KPI cards).
  const explicitFormatFor = (measure: string) =>
    explicitFormats.get(measure) ?? explicitFormatsByDisplayName.get(formatFieldDisplayName(measure))

  const valueFormats: Record<string, InsightValueFormat> = {}
  const formatFor = (measure: string) => {
    const resolved = valueFormats[measure]
      ?? resolveInsightValueFormat(measure, explicitFormatFor(measure)?.type, resolveColumnFormat(data.columnFormats, measure))
    valueFormats[measure] = resolved
    return resolved
  }

  // A stored numeric format keeps its decimals and currency symbol; otherwise the insight kind decides.
  const formatValue = (value: string | number, measure: string) => {
    const kind = formatFor(measure)
    const stored = resolveColumnFormat(data.columnFormats, measure)
    return stored && isNumericFormatKind(stored.kind)
      ? formatColumnValue(value, measure, data.columnFormats)
      : formatInsightValue(value, kind)
  }

  const chartData = data.chartData.map((point) => {
    const series = point.series?.length ? point.series : [{ name: 'value', value: point.value }]
    return {
      label: point.label,
      ...Object.fromEntries(series.map((entry) => [
        formatFieldDisplayName(entry.name),
        formatValue(entry.value, entry.name),
      ])),
    }
  })

  const kpiCards = data.kpiCards.map((card) => {
    const measure = card.measureKey || card.measureLabel || card.label
    const caption = card.displayLabel ?? card.measureLabel
    const appendCaption = caption && card.label && card.label !== card.measureLabel && card.label !== caption
    return {
      label: appendCaption ? `${card.label} (${caption})` : (card.displayLabel ?? card.label),
      value: (card.format ? formatFieldValue(card.value, card.format) : formatValue(card.value, measure)) + (card.unit ? ` ${card.unit}` : ''),
    }
  })

  return {
    chartData,
    kpiCards,
    valueFormats: Object.fromEntries(Object.entries(valueFormats).map(([measure, format]) => [formatFieldDisplayName(measure), format])),
  }
}
